// Generic offender-list table for the Workflow Hygiene drill-down tab
// (PRODOPSAI-385). Shared across the three drill sources (unlinked items,
// non-spine statuses, non-canonical types) so per-source files stay tiny —
// each one supplies columns + types and the paginator + layout live here.
//
// Server-side pagination: `data.pagination` carries totalCount; this
// component renders Prev/Next controls and surfaces the active page. The
// caller refetches via `onPageChange(nextPage)`.

import type { ReactNode } from 'react';
import { Card, CardContent, ErrorBanner, Spinner } from '@/components/ui';
import type { IDrillPagination } from '@skystone/shared';

export interface DrillColumn<T> {
  /** Stable key for React reconciliation on header + cell. */
  key: string;
  label: string;
  /** Renders the cell content for one row. */
  render: (row: T) => ReactNode;
  /** Optional Tailwind class applied to both header and cells in this column. */
  className?: string;
}

interface DrillTableProps<T> {
  columns: DrillColumn<T>[];
  rows: T[];
  /** Stable, unique React key for a row (avoids array-index keys; e.g. the issue key). */
  rowKey: (row: T) => string;
  pagination: IDrillPagination;
  onPageChange: (page: number) => void;
  loading: boolean;
  error: string | null;
  /** Copy shown when the response succeeds but rows is empty (no offenders). */
  emptyMessage: string;
}

function totalPages(pagination: IDrillPagination): number {
  if (pagination.totalCount === 0) return 1;
  return Math.ceil(pagination.totalCount / pagination.pageSize);
}

function pageRangeLabel(pagination: IDrillPagination, rowsOnPage: number): string {
  if (pagination.totalCount === 0) return '0 of 0';
  const start = (pagination.page - 1) * pagination.pageSize + 1;
  const end = start + rowsOnPage - 1;
  return `${start}–${end} of ${pagination.totalCount}`;
}

export function DrillTable<T>({
  columns,
  rows,
  rowKey,
  pagination,
  onPageChange,
  loading,
  error,
  emptyMessage,
}: Readonly<DrillTableProps<T>>) {
  if (loading) {
    return <div className="flex h-40 items-center justify-center"><Spinner /></div>;
  }
  if (error) {
    return <ErrorBanner message={error} />;
  }
  if (rows.length === 0) {
    return (
      <Card>
        <CardContent className="p-6 text-center text-sm text-muted-foreground">{emptyMessage}</CardContent>
      </Card>
    );
  }

  const last = totalPages(pagination);

  return (
    <Card>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                {columns.map((c) => (
                  <th key={c.key} className={`px-4 py-2 font-semibold ${c.className ?? ''}`}>{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={rowKey(row)} className="border-t border-border">
                  {columns.map((c) => (
                    <td key={c.key} className={`px-4 py-2 ${c.className ?? ''}`}>{c.render(row)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between border-t border-border px-4 py-2 text-xs text-muted-foreground">
          <span>{pageRangeLabel(pagination, rows.length)}</span>
          <span className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => onPageChange(pagination.page - 1)}
              disabled={pagination.page <= 1}
              className="rounded border border-border px-2 py-1 disabled:cursor-not-allowed disabled:opacity-40"
            >
              ← Prev
            </button>
            <span>Page {pagination.page} of {last}</span>
            <button
              type="button"
              onClick={() => onPageChange(pagination.page + 1)}
              disabled={pagination.page >= last}
              className="rounded border border-border px-2 py-1 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Next →
            </button>
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
